import { useEffect, useState } from "react";
import { X, Check, Camera, Trash2, Loader2 } from "lucide-react";
import type { CreateSpotInput, SpotKind } from "@/lib/types";
import { createSpot, uploadSpotPhoto } from "@/lib/api";
import { useLegality } from "@/hooks/useLegality";
import { LegalityBadge } from "./LegalityBadge";
import { t } from "@/lib/i18n";

/**
 * Create-spot bottom sheet (spec 06 §2 screen 3).
 *
 * Opens in the *details* phase after the user has positioned the pin. The
 * legality badge reflects the (now fixed) pin coordinates. Legality is advisory
 * only — the user can always submit, even in a no-consumption zone (the badge
 * warns but never hard-blocks). On submit the spot is created with
 * `status='pending'` (migration 20260115000012) and any selected photos are
 * uploaded to the `spot-photos` bucket and linked.
 */
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // matches the bucket file_size_limit
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/avif"];

export interface CreateSpotProps {
  /** Current pin coordinates (fixed once the details sheet is open). */
  lng: number;
  lat: number;
  onClose: () => void;
  onCreated: (spotId: string) => void;
}

export function CreateSpot({ lng, lat, onClose, onCreated }: CreateSpotProps) {
  const { state, result, loading } = useLegality(lng, lat);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<SpotKind>("outdoor");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Revoke object URLs on unmount to avoid leaking the previews.
  useEffect(() => {
    return () => {
      previews.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Legality is advisory only — never hard-block submission.
  const blocked = loading;

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    setPhotoError(null);
    const next: File[] = [];
    for (const f of Array.from(files)) {
      if (photos.length + next.length >= MAX_PHOTOS) break;
      if (!ACCEPTED.includes(f.type)) {
        setPhotoError(t("create.photoTypeError"));
        continue;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        setPhotoError(t("create.photoSizeError"));
        continue;
      }
      next.push(f);
    }
    if (next.length === 0) return;
    setPhotos((prev) => [...prev, ...next]);
    setPreviews((prev) => [...prev, ...next.map((f) => URL.createObjectURL(f))]);
  };

  const removePhoto = (index: number) => {
    setPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (blocked || submitting || name.trim().length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateSpotInput = { kind, name, description, lng, lat };
      const spot = await createSpot(input);

      // Best-effort photo upload after the spot exists. Failures are surfaced
      // but do not roll back the spot — the spot is valid without photos.
      if (photos.length > 0) {
        setUploadProgress(
          t("create.photoUploading", { n: "0", total: String(photos.length) }),
        );
        let done = 0;
        for (const file of photos) {
          try {
            await uploadSpotPhoto(spot.id, file);
            done += 1;
            setUploadProgress(
              t("create.photoUploading", {
                n: String(done),
                total: String(photos.length),
              }),
            );
          } catch {
            // keep going; partial upload is acceptable
          }
        }
      }

      onCreated(spot.id);
    } catch (e) {
      setError(
        t("create.error", {
          message: e instanceof Error ? e.message : t("errors.generic"),
        }),
      );
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 sheet-up">
      <button
        type="button"
        aria-label={t("create.cancel")}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 fade-in"
      />
      <div
        className="relative mx-auto max-h-[90vh] max-w-lg overflow-y-auto rounded-t-[var(--radius-sheet)] bg-bg-elevated p-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] shadow-2xl"
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-3 sheet-grabber" />

        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-fg">{t("create.title")}</h2>
            <p className="text-sm text-fg-muted">{t("create.instructions")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-3 text-fg-muted hover:bg-surface"
            aria-label={t("create.cancel")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Live legality badge (debounced via useLegality). */}
        <div className="mt-4">
          <LegalityBadge
            state={state}
            result={result}
            loading={loading}
            variant="card"
          />
        </div>

        {/* Form */}
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-fg-muted">
              {t("create.fieldName")}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder={t("create.fieldNamePlaceholder")}
              className="w-full rounded-lg bg-bg p-2.5 text-sm text-fg outline-none ring-1 ring-line focus:ring-accent"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-fg-muted">
              {t("create.fieldKind")}
            </label>
            <div className="flex gap-2">
              {(["outdoor", "indoor_club"] as SpotKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`flex-1 rounded-lg py-2 text-sm font-medium ring-1 transition ${
                    kind === k
                      ? "bg-accent text-accent-contrast ring-accent"
                      : "bg-surface text-fg ring-line"
                  }`}
                >
                  {t(`spot.kind.${k}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-fg-muted">
              {t("create.fieldDescription")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder={t("create.fieldDescriptionPlaceholder")}
              className="w-full resize-none rounded-lg bg-bg p-2.5 text-sm text-fg outline-none ring-1 ring-line focus:ring-accent"
            />
          </div>

          {/* Photo upload */}
          <div>
            <label className="mb-1 block text-xs font-medium text-fg-muted">
              {t("create.fieldPhotos")} ({photos.length}/{MAX_PHOTOS})
            </label>
            <div className="flex flex-wrap gap-2">
              {previews.map((url, i) => (
                <div key={url} className="relative">
                  <img
                    src={url}
                    alt=""
                    className="h-16 w-16 rounded-lg object-cover ring-1 ring-line"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-legal-red text-white shadow"
                    aria-label={t("create.photoRemove")}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS ? (
                <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg bg-surface text-fg-muted ring-1 ring-dashed ring-line hover:text-fg">
                  <Camera size={18} />
                  <span className="text-[11px] leading-none">
                    {t("create.photoAdd")}
                  </span>
                  <input
                    type="file"
                    accept={ACCEPTED.join(",")}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addPhotos(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              ) : null}
            </div>
            {photoError ? (
              <p className="mt-1 text-xs text-legal-red">{photoError}</p>
            ) : null}
            <p className="mt-1 text-xs text-fg-subtle">
              {t("create.photoHint")}
            </p>
          </div>
        </div>

        {/* Moderation notice: new spots are reviewed before they go public. */}
        <p className="mt-3 rounded-lg bg-surface p-2.5 text-xs text-fg-muted">
          {t("create.moderationNotice")}
        </p>

        {error ? (
          <p className="mt-3 text-sm text-legal-red">{error}</p>
        ) : null}

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg bg-surface py-2.5 text-sm font-medium text-fg ring-1 ring-line"
          >
            {t("create.cancel")}
          </button>
          <button
            type="button"
            disabled={blocked || submitting || name.trim().length === 0}
            onClick={submit}
            className="flex flex-[2] items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-sm font-semibold text-accent-contrast disabled:opacity-40"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Check size={16} />
            )}
            {uploadProgress
              ? uploadProgress
              : blocked
                ? t("create.submitBlocked")
                : submitting
                  ? t("create.submitting")
                  : t("create.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
